import { createServerFn } from "@tanstack/react-start";
import { generateText, Output } from "ai";
import { z } from "zod";

const InputSchema = z.object({
  trade: z.enum(["plumber", "electrician", "hvac", "roofer", "other"]),
  city: z.string().trim().min(1).max(80),
  monthlyMarketingSpend: z.number().min(0).max(100000),
  averageJobValue: z.number().min(50).max(100000),
  closeRate: z.number().min(1).max(100),
  currentMonthlyLeads: z.number().min(0).max(5000),
});

export type RoiInput = z.infer<typeof InputSchema>;

const Result = z.object({
  projectedLeadsPerMonth: z.number(),
  projectedJobsPerMonth: z.number(),
  projectedRevenuePerMonth: z.number(),
  projectedRoiMultiple: z.number(),
  paybackMonths: z.number(),
  summary: z.string(),
  recommendations: z.array(z.string()),
  assumptions: z.array(z.string()),
});

export type RoiResult = z.infer<typeof Result>;

export const projectRoi = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<RoiResult> => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("AI service is not configured.");

    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const gateway = createLovableAiGatewayProvider(apiKey);

    const prompt = `You are a senior marketing analyst for trades contractors in Utah.
Project realistic 12-month ROI for this contractor based on industry benchmarks.

Inputs:
- Trade: ${data.trade}
- City: ${data.city}
- Current monthly marketing spend: $${data.monthlyMarketingSpend}
- Average job value: $${data.averageJobValue}
- Lead close rate: ${data.closeRate}%
- Current monthly leads: ${data.currentMonthlyLeads}

Use these benchmarks:
- Trades SEO + Google Ads programs typically lift qualified leads 60-180% inside 6 months when the baseline is light.
- HVAC CPL: $45-120. Plumbing CPL: $35-95. Electrical CPL: $40-110. Roofing CPL: $60-220.
- Map Pack movement begins at 60-90 days; meaningful lead volume at month 4-6.

Return numbers grounded in those benchmarks. Be conservative.
- projectedLeadsPerMonth: month 6 projection
- projectedJobsPerMonth: leads * (closeRate/100)
- projectedRevenuePerMonth: jobs * averageJobValue
- projectedRoiMultiple: projectedRevenuePerMonth / max(monthlyMarketingSpend, 1)
- paybackMonths: months until projectedRevenuePerMonth exceeds cumulative spend
- summary: 2-3 sentence plain-English summary
- recommendations: 3-5 short action items specific to the trade and city
- assumptions: 2-4 bullet assumptions used in the projection`;

    const { output } = await generateText({
      model: gateway("google/gemini-3-flash-preview"),
      output: Output.object({ schema: Result }),
      prompt,
    });

    return output as RoiResult;
  });